// SPDX-License-Identifier: GPL-3.0-or-later
// The boss of a stage on the table. The boss is one large SigmaBot. It hovers behind the four DENY
// targets, so the ball and the targets stay in view. The layer reads the boss feedback events and
// draws the boss. It never changes the rules, the physics or a score.
//
// Play is calm. Every effect is local: a few small sparks, a short glow on the boss, a bubble, a
// disc on the table. There is no camera move, no full-screen object and no text in the scene.
//
// All time comes from the `dtMs` that the caller passes to `update`. A frozen frame (dt 0)
// freezes the boss too. The layer has no clock and no random source.
//
// Modes (from the effects policy):
// - full: entrance, bob, bubble, disc, sparks, glow, sink-out.
// - reduced: no sparks, no glow, no bob and no entrance. The bubble is static. The disc stays.
// - off: the model only. The HUD boss bar tells the state.

import { Group, type Mesh, type MeshBasicMaterial, type Object3D } from "three";
import type { FeedbackEvent } from "../../shared/feedback/index.ts";
import { buildSigmaBot } from "../../table/characters/sigmabot/index.ts";
import { disposeObject } from "../scene.ts";
import { colorOf, TOKEN_HEX } from "../vfx/palette.ts";
import type { ParticlePool } from "../vfx/particles.ts";
import type { VfxPolicy } from "../vfx/policy.ts";
import { type BossView, fadeStep, holdStep, modelHidesAnyBall } from "./occlusion.ts";
import {
  applyFade,
  applyTint,
  type BossSize,
  type FadeSlot,
  fadeSlots,
  makeShield,
  makeTelegraph,
  SHIELD_OPACITY,
  sizeOf,
  type Telegraph,
  type TintSlot,
  tintSlots,
} from "./parts.ts";
import { DEFEAT_SHAPE, HIT_SHAPE } from "./shapes.ts";
import { bossLook } from "./specs.ts";

export type { BossView } from "./occlusion.ts";
export { BOSS_LOOKS, type BossLook, bossLook } from "./specs.ts";

/** The entrance takes this long. */
export const ENTER_MS = 600;
/** The sink-out after a defeat takes this long. */
export const LEAVE_MS = 800;
/** Two hit effects are at least this far apart. */
export const HIT_GAP_MS = 220;
/** The glow of a hit lasts this long. */
export const HIT_TINT_MS = 160;
/** The glow of a phase change lasts this long. */
export const PHASE_TINT_MS = 400;
/** The dull flicker of the shield after a blocked hit lasts this long. */
export const FLICKER_MS = 160;
/** The ring that goes out from a defeated boss lasts this long. */
export const DEFEAT_RING_MS = 600;
/** The hover height of the boss above the playfield, in table units. */
export const BOSS_LIFT = 70;
/** The boss stands this far behind the centre of the DENY targets (toward the top). */
export const BOSS_BACK = 130;
/** The bob goes up and down by this much. The period matches the horde boss. */
const BOB_AMPLITUDE = 8;
const BOB_PERIOD_MS = 520;
/** The entrance starts this far below its end height, and at this share of the size. */
const ENTER_DROP = 110;
const ENTER_SCALE_FROM = 0.4;
/** The sink-out goes this far down, and ends at this share of the size. */
const LEAVE_DROP = 150;
const LEAVE_SCALE_TO = 0.1;
/** Sparks of a hit and of a defeat. */
const HIT_SPARKS = 4;
const DEFEAT_SPARKS = 24;
/** Strength of the glow of a hit and of a phase change, from 0 to 1. */
const HIT_GLOW = 0.6;
const PHASE_GLOW = 0.7;

/** The feedback events that remove the boss at once: a new stage or the end of the game. */
const RESET_KINDS: ReadonlySet<FeedbackEvent["kind"]> = new Set(["stage_start", "game_over"]);

/** How much the layer draws. */
export type BossMode = "full" | "reduced" | "off";

/** The mode of a policy. Off has no bursts. Reduced has no character reactions. */
export function bossMode(policy: VfxPolicy): BossMode {
  if (policy.bursts === 0) return "off";
  return policy.reactions ? "full" : "reduced";
}

/** What the layer needs. */
export interface BossDeps {
  /** The group that gets the boss group. */
  readonly parent: Object3D;
  /** The particle pool for the sparks. */
  readonly pool: Pick<ParticlePool, "burst">;
  /** The centre of the DENY targets in the scene frame (x right, z toward the player). */
  readonly anchor: { readonly x: number; readonly z: number };
}

/** The boss layer. */
export interface BossLayer {
  /** React to one feedback event. Events that are not about the boss do nothing. */
  onEvent(ev: FeedbackEvent, policy: VfxPolicy): void;
  /**
   * Move the boss on by `dtMs`. A dt of 0 changes nothing. With a view, the boss fades while it
   * hides a ball, so a ball on the playfield is always in sight.
   */
  update(dtMs: number, policy: VfxPolicy, view?: BossView): void;
  /** True while a boss model is in the scene (also while it sinks out). */
  readonly active: boolean;
  /** The progress of the wind-up from 0 to 1. 0 when there is no wind-up. */
  readonly telegraph: number;
  /** Remove every object and free the GPU data. Later calls do nothing. */
  dispose(): void;
}

/** One boss that is in the scene. */
interface Body {
  readonly rig: Group;
  readonly model: Group;
  readonly shield: Mesh;
  readonly tints: readonly TintSlot[];
  readonly fades: readonly FadeSlot[];
  /** The opacity of the model, from 0 to 1. It falls while the boss hides a ball. */
  alpha: number;
  /** The time that the boss stays faded after the last hide. 0 when there is no hold. */
  hideHoldMs: number;
  readonly size: BossSize;
  enterMs: number;
  bobMs: number;
  sinceHitMs: number;
  glowMs: number;
  glowTotalMs: number;
  glowHex: number;
  glowPeak: number;
  flickerMs: number;
  shieldOn: boolean;
  shieldMs: number;
  windMs: number;
  windTotalMs: number;
  /** Time since the defeat. -1 while the boss lives. */
  leaveMs: number;
}

/** The state of the layer. */
interface Ctx {
  readonly deps: BossDeps;
  readonly root: Group;
  readonly disc: Telegraph;
  body: Body | null;
  /** Time since the defeat ring started. -1 when there is no ring. */
  ringMs: number;
  disposed: boolean;
}

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));
const easeOut = (t: number): number => 1 - (1 - t) ** 3;
const cleanDt = (dt: number): number => (Number.isFinite(dt) && dt > 0 ? Math.min(dt, 100) : 0);

function removeBody(ctx: Ctx): void {
  const b = ctx.body;
  if (b === null) return;
  ctx.root.remove(b.rig);
  disposeObject(b.rig);
  ctx.body = null;
}

function startBody(ctx: Ctx, name: string): void {
  removeBody(ctx);
  const look = bossLook(name);
  const model = buildSigmaBot(look.spec, look.scale);
  const size = sizeOf(model);
  const shield = makeShield(size);
  const rig = new Group();
  rig.name = "BossRig";
  rig.add(model, shield);
  ctx.root.add(rig);
  ctx.body = {
    rig,
    model,
    shield,
    tints: tintSlots(model),
    fades: fadeSlots(model),
    alpha: 1,
    hideHoldMs: 0,
    size,
    enterMs: 0,
    bobMs: 0,
    sinceHitMs: HIT_GAP_MS,
    glowMs: 0,
    glowTotalMs: 1,
    glowHex: TOKEN_HEX.white,
    glowPeak: 0,
    flickerMs: 0,
    shieldOn: false,
    shieldMs: 0,
    windMs: 0,
    windTotalMs: 0,
    leaveMs: -1,
  };
}

/** The middle of the boss in the scene frame. Sparks start here. */
function centreOf(ctx: Ctx, b: Body): { x: number; y: number; z: number } {
  const { anchor } = ctx.deps;
  const y = b.rig.position.y + b.size.height * 0.5 * b.rig.scale.y;
  return { x: anchor.x, y, z: anchor.z - BOSS_BACK };
}

function glow(b: Body, hex: number, ms: number, peak: number): void {
  b.glowHex = hex;
  b.glowMs = ms;
  b.glowTotalMs = ms;
  b.glowPeak = peak;
}

function onHit(ctx: Ctx, blocked: boolean, mode: BossMode): void {
  const b = ctx.body;
  if (b === null || b.leaveMs >= 0 || mode !== "full") return;
  if (b.sinceHitMs < HIT_GAP_MS) return;
  b.sinceHitMs = 0;
  if (blocked) {
    b.flickerMs = FLICKER_MS;
    return;
  }
  glow(b, TOKEN_HEX.block, HIT_TINT_MS, HIT_GLOW);
  const c = centreOf(ctx, b);
  ctx.deps.pool.burst(c.x, c.y, c.z, colorOf(TOKEN_HEX.block), HIT_SPARKS, HIT_SHAPE);
}

function onPhase(ctx: Ctx, mode: BossMode): void {
  const b = ctx.body;
  if (b === null || b.leaveMs >= 0) return;
  // A phase change stops a wind-up.
  b.windTotalMs = 0;
  if (mode === "full") glow(b, TOKEN_HEX.ask, PHASE_TINT_MS, PHASE_GLOW);
}

function onWindUp(ctx: Ctx, ms: number): void {
  const b = ctx.body;
  if (b === null || b.leaveMs >= 0) return;
  b.windMs = 0;
  b.windTotalMs = Number.isFinite(ms) && ms > 0 ? ms : 0;
}

function onShield(ctx: Ctx, on: boolean, ms: number): void {
  const b = ctx.body;
  if (b === null || b.leaveMs >= 0) return;
  b.shieldOn = on;
  b.shieldMs = on && Number.isFinite(ms) && ms > 0 ? ms : Number.POSITIVE_INFINITY;
  // The wind-up ends when the shield comes.
  if (on) b.windTotalMs = 0;
}

function onDefeat(ctx: Ctx, mode: BossMode): void {
  const b = ctx.body;
  if (b === null || b.leaveMs >= 0) return;
  if (mode !== "full") {
    removeBody(ctx);
    return;
  }
  b.leaveMs = 0;
  b.shieldOn = false;
  b.windTotalMs = 0;
  const c = centreOf(ctx, b);
  ctx.deps.pool.burst(c.x, c.y, c.z, colorOf(TOKEN_HEX.ask), DEFEAT_SPARKS, DEFEAT_SHAPE);
  ctx.ringMs = 0;
}

function handle(ctx: Ctx, ev: FeedbackEvent, mode: BossMode): void {
  switch (ev.kind) {
    case "boss_start":
      startBody(ctx, ev.name);
      // Pose the boss now, so it never shows for one frame at the origin.
      updateBody(ctx, 0, mode);
      return;
    case "boss_hit":
      onHit(ctx, ev.blocked, mode);
      return;
    case "boss_phase":
      onPhase(ctx, mode);
      return;
    case "boss_telegraph":
      onWindUp(ctx, ev.ms);
      return;
    case "boss_shield":
      onShield(ctx, ev.on, ev.ms);
      return;
    case "boss_defeat":
      onDefeat(ctx, mode);
      return;
    default:
  }
}

/** Count the timers down by `dt`. */
function advance(b: Body, dt: number): void {
  b.enterMs = Math.min(ENTER_MS, b.enterMs + dt);
  b.bobMs += dt;
  b.sinceHitMs = Math.min(HIT_GAP_MS, b.sinceHitMs + dt);
  b.glowMs = Math.max(0, b.glowMs - dt);
  b.flickerMs = Math.max(0, b.flickerMs - dt);
  if (b.leaveMs >= 0) b.leaveMs += dt;
  if (b.shieldOn && Number.isFinite(b.shieldMs)) {
    b.shieldMs -= dt;
    if (b.shieldMs <= 0) b.shieldOn = false;
  }
  if (b.windTotalMs > 0) {
    b.windMs += dt;
    if (b.windMs >= b.windTotalMs) b.windTotalMs = 0;
  }
}

/** Put the rig at its place and size: entrance, bob and sink-out. */
function pose(ctx: Ctx, b: Body, mode: BossMode): void {
  const full = mode === "full";
  const enter = full ? easeOut(b.enterMs / ENTER_MS) : 1;
  let scale = ENTER_SCALE_FROM + (1 - ENTER_SCALE_FROM) * enter;
  let y = BOSS_LIFT - (1 - enter) * ENTER_DROP;
  if (b.leaveMs >= 0) {
    const k = clamp01(b.leaveMs / LEAVE_MS);
    scale *= 1 - k * (1 - LEAVE_SCALE_TO);
    y -= k * LEAVE_DROP;
  }
  const bob = full ? Math.abs(Math.sin(b.bobMs / BOB_PERIOD_MS)) * BOB_AMPLITUDE : 0;
  b.rig.position.set(ctx.deps.anchor.x, y + bob, ctx.deps.anchor.z - BOSS_BACK);
  b.rig.scale.setScalar(scale);
}

function shieldLook(b: Body, mode: BossMode): void {
  const show = b.shieldOn && mode !== "off" && b.leaveMs < 0;
  b.shield.visible = show;
  if (!show) return;
  const material = b.shield.material as MeshBasicMaterial;
  let opacity = SHIELD_OPACITY;
  if (mode === "full") opacity += 0.06 * Math.sin(b.bobMs / 180);
  if (b.flickerMs > 0) opacity *= 1 - 0.6 * Math.sin((Math.PI * b.flickerMs) / FLICKER_MS);
  material.opacity = opacity;
}

function glowLook(b: Body, mode: BossMode): void {
  const live = mode === "full" && b.glowMs > 0;
  const amount = live ? b.glowPeak * (b.glowMs / b.glowTotalMs) : 0;
  applyTint(b.tints, b.glowHex, amount);
}

/** The wind-up disc: it fills with the progress. It shows in full and reduced mode. */
function discLook(ctx: Ctx, mode: BossMode): void {
  const { fill, ring } = ctx.disc;
  const b = ctx.body;
  const winding = b !== null && b.windTotalMs > 0 && mode !== "off";
  const dying = ctx.ringMs >= 0;
  fill.visible = winding;
  ring.visible = winding || dying;
  const ringMaterial = ring.material as MeshBasicMaterial;
  if (winding && b !== null) {
    fill.scale.setScalar(Math.max(0.001, clamp01(b.windMs / b.windTotalMs)));
    ring.scale.setScalar(1);
    ringMaterial.opacity = 0.6;
  } else if (dying) {
    const k = clamp01(ctx.ringMs / DEFEAT_RING_MS);
    ring.scale.setScalar(0.4 + 0.8 * k);
    ringMaterial.opacity = 0.6 * (1 - k);
  }
}

function updateRing(ctx: Ctx, dt: number): void {
  if (ctx.ringMs < 0) return;
  ctx.ringMs += dt;
  if (ctx.ringMs >= DEFEAT_RING_MS) ctx.ringMs = -1;
}

/**
 * Fade the boss while it hides a ball, and for a hold time after the last hide. The fade out is
 * at once. The fade in waits for the hold, so a ball on the edge does not make the boss pulse.
 * Without a view the boss stays as it is.
 */
function fadeLook(b: Body, dt: number, view: BossView | undefined): void {
  if (view === undefined) return;
  b.rig.updateMatrixWorld(true);
  b.hideHoldMs = holdStep(b.hideHoldMs, modelHidesAnyBall(view, b.model), dt);
  const alpha = fadeStep(b.alpha, b.hideHoldMs > 0, dt);
  if (alpha === b.alpha) return;
  b.alpha = alpha;
  applyFade(b.fades, alpha);
}

function updateBody(ctx: Ctx, dt: number, mode: BossMode, view?: BossView): void {
  const b = ctx.body;
  if (b === null) return;
  advance(b, dt);
  if (b.leaveMs >= LEAVE_MS || (b.leaveMs >= 0 && mode !== "full")) {
    removeBody(ctx);
    return;
  }
  pose(ctx, b, mode);
  shieldLook(b, mode);
  glowLook(b, mode);
  fadeLook(b, dt, view);
}

/** Make the boss layer. The boss group goes into `deps.parent`. */
export function createBossLayer(deps: BossDeps): BossLayer {
  const root = new Group();
  root.name = "Boss";
  const disc = makeTelegraph();
  disc.group.position.set(deps.anchor.x, 0, deps.anchor.z);
  root.add(disc.group);
  deps.parent.add(root);
  const ctx: Ctx = { deps, root, disc, body: null, ringMs: -1, disposed: false };

  return {
    onEvent(ev, policy) {
      if (ctx.disposed) return;
      if (RESET_KINDS.has(ev.kind)) {
        removeBody(ctx);
        return;
      }
      handle(ctx, ev, bossMode(policy));
    },
    update(dtMs, policy, view) {
      if (ctx.disposed) return;
      const mode = bossMode(policy);
      const dt = cleanDt(dtMs);
      updateRing(ctx, dt);
      updateBody(ctx, dt, mode, view);
      discLook(ctx, mode);
    },
    get active() {
      return ctx.body !== null;
    },
    get telegraph() {
      const b = ctx.body;
      return b !== null && b.windTotalMs > 0 ? clamp01(b.windMs / b.windTotalMs) : 0;
    },
    dispose() {
      if (ctx.disposed) return;
      ctx.disposed = true;
      removeBody(ctx);
      deps.parent.remove(root);
      disposeObject(root);
    },
  };
}
